import React, { useMemo } from "react";
import { ChevronRight, Download, Printer, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { AmplifyLogo } from "@/components/AmplifyLogo";
import { fmt, calculate } from "@/lib/calculations";

// Helper functions to replace nested ternaries
const getAdminColumnHeader = (isAdp, adpUnbundled) => {
  if (isAdp && !adpUnbundled) return "Svc Fee *";
  if (isAdp && adpUnbundled) return "Svc Fee";
  return "Admin";
};

const getSavingsColor = (amount) => {
  if (amount > 0) return "text-emerald-600";
  if (amount < 0) return "text-red-500";
  return "";
};

const getCurrentAdminAmount = (isAdp, adpUnbundled, currentTotals, currentAdminFee, totalServiceFees) => {
  if (isAdp && !adpUnbundled) return currentTotals.wcPremium + currentAdminFee;
  if (isAdp && adpUnbundled) return totalServiceFees;
  return currentAdminFee;
};

export const ProposalView = ({
  setPage,
  exportProgress,
  printProposal,
  downloadPDF,
  downloadWord,
  downloadExcel,
  isExporting,
  quoteRef,
  clientName,
  lineItems,
  preparedBy,
  quoteDate,
  savings,
  savingsPct,
  currentProvider,
  currentTotals,
  proposedTotals,
  currentAdminFee,
  proposedAdminFee,
  currentTotal,
  proposedTotal,
  isAdp,
  adpUnbundled,
  totalServiceFees,
  serviceFees,
  proposedItems,
  sutaBases,
  isClientState,
  MONOPOLISTIC_STATES,
}) => {
  // Memoize expensive sort/join operations
  const statesList = useMemo(() => {
    if (lineItems.length === 0) return '';
    return [...new Set(lineItems.map(i => i.state))].sort().join(", ");
  }, [lineItems]);

  return (
    <div className="space-y-6" data-testid="proposal-page">
      {/* Export Buttons */}
      <div className="flex items-center justify-between">
        <button 
          data-testid="back-to-builder-btn" 
          onClick={() => setPage(1)}
          className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium transition-colors"
        >
          <ChevronRight size={16} className="rotate-180" /> Back to Builder
        </button>
        <div className="flex items-center gap-2">
          {exportProgress && (
            <span className="flex items-center gap-1.5 text-sm text-[#3c68ff] font-medium" data-testid="export-progress">
              <Loader2 size={14} className="animate-spin" /> {exportProgress}
            </span>
          )}
          <button 
            data-testid="print-proposal-btn" 
            onClick={printProposal}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all print:hidden"
          >
            <Printer size={15} /> Print
          </button>
          <button 
            data-testid="download-pdf-btn" 
            onClick={downloadPDF} 
            disabled={isExporting}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={15} /> PDF
          </button>
          <button 
            data-testid="download-word-btn" 
            onClick={downloadWord} 
            disabled={isExporting}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileText size={15} /> Word
          </button>
          <button 
            data-testid="download-excel-btn" 
            onClick={downloadExcel} 
            disabled={isExporting}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-[#3c68ff] text-white rounded-lg text-sm font-medium hover:bg-[#2b4bca] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet size={15} /> Excel
          </button>
        </div>
      </div>

      {/* Proposal Content */}
      <div ref={quoteRef} id="print-proposal" className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="proposal-content">
        {/* Header */}
        <div className="p-8 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AmplifyLogo size={40} />
              <div>
                <div className="text-2xl font-bold font-['Outfit'] text-slate-900">Cost Comparison Proposal</div>
                <div className="text-sm text-slate-500 mt-0.5">{clientName || "Client"}</div>
                {statesList && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {statesList}
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              {preparedBy && (
                <div className="text-sm text-slate-500">
                  Prepared by: <strong className="text-slate-700">{preparedBy}</strong>
                </div>
              )}
              <div className="text-sm text-slate-500 mt-0.5">
                Date Prepared: <strong className="text-slate-700">{quoteDate}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Savings Banner */}
        {savings > 0 && (
          <div className="mx-8 mt-6 p-5 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="savings-banner">
            <div className="text-center">
              <div className="text-sm font-medium text-emerald-600 uppercase tracking-wider">Projected Annual Savings</div>
              <div className="text-4xl font-bold font-mono text-emerald-700 mt-1">{fmt(savings)}</div>
              <div className="text-lg font-semibold text-emerald-600 mt-0.5">{savingsPct}% reduction in total costs</div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-6 p-8">
          {/* Current Provider Card */}
          <div className="border-2 border-red-300 rounded-xl p-5 bg-red-50/40" data-testid="current-summary-card">
            <div className="text-xs font-bold text-red-600 uppercase tracking-widest mb-3">
              {currentProvider || "Current Provider"}
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-red-400">Gross Wages</span>
                <span className="font-mono font-semibold text-red-700">{fmt(currentTotals.grossWages)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-red-400">Employee Count</span>
                <span className="font-mono font-semibold text-red-700">{currentTotals.employees}</span>
              </div>
              {isAdp && !adpUnbundled ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">ADP Service Fee <span className="text-[10px] font-normal align-top">*</span></span>
                    <span className="font-mono font-semibold text-red-700">{fmt(currentTotals.wcPremium + currentAdminFee)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">SUTA Tax</span>
                    <span className="font-mono font-semibold text-red-700">{fmt(currentTotals.sutaTax)}</span>
                  </div>
                </>
              ) : isAdp && adpUnbundled ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">Workers' Compensation</span>
                    <span className="font-mono font-semibold text-red-700">{fmt(currentTotals.wcPremium)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">ADP Service Fee</span>
                    <span className="font-mono font-semibold text-red-700">{fmt(totalServiceFees)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">SUTA Tax</span>
                    <span className="font-mono font-semibold text-red-700">{fmt(currentTotals.sutaTax)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">Admin Fee</span>
                    <span className="font-mono font-semibold text-red-700">{fmt(currentAdminFee)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">SUTA Tax</span>
                    <span className="font-mono font-semibold text-red-700">{fmt(currentTotals.sutaTax)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-400">Workers' Compensation</span>
                    <span className="font-mono font-semibold text-red-700">{fmt(currentTotals.wcPremium)}</span>
                  </div>
                </>
              )}
              <div className="mt-1 pt-2 border-t border-red-200 flex justify-between text-base font-bold text-red-700">
                <span>Total</span>
                <span className="font-mono">{fmt(currentTotal)}</span>
              </div>
              {isAdp && !adpUnbundled && (
                <p className="text-[10px] text-red-400">* ADP Service Fee includes bundled Workers' Comp and Admin Fee</p>
              )}
            </div>
          </div>

          {/* Amplify HR Card */}
          <div className="border-2 border-[#3c68ff] rounded-xl p-5 bg-[#3c68ff]/[0.02]" data-testid="amplify-summary-card">
            <div className="text-xs font-bold text-[#3c68ff] uppercase tracking-widest mb-3 flex items-center gap-2">
              <AmplifyLogo size={14} /> Amplify HR
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Gross Wages</span>
                <span className="font-mono font-semibold">{fmt(proposedTotals.grossWages)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Employee Count</span>
                <span className="font-mono font-semibold">{proposedTotals.employees}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Admin Fee</span>
                <span className="font-mono font-semibold">{fmt(proposedAdminFee)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">SUTA Tax</span>
                <span className="font-mono font-semibold">{fmt(proposedTotals.sutaTax)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Workers' Compensation</span>
                <span className="font-mono font-semibold">{fmt(proposedTotals.wcPremium)}</span>
              </div>
              <div className="mt-1 pt-2 border-t border-[#3c68ff]/20 flex justify-between text-base font-bold text-[#3c68ff]">
                <span>Total</span>
                <span className="font-mono">{fmt(proposedTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Comparison Table */}
        <div className="px-8 pb-8">
          <h3 className="text-base font-semibold font-['Outfit'] text-slate-900 mb-3">Line-by-Line Comparison</h3>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wider bg-slate-50" rowSpan={2}>State</th>
                  <th className="px-3 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wider bg-slate-50" rowSpan={2}>Code</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-slate-500 uppercase tracking-wider bg-slate-50" rowSpan={2}>EEs</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-slate-500 uppercase tracking-wider bg-slate-50" rowSpan={2}>Wages</th>
                  <th className="px-3 py-2 text-center font-semibold text-red-700 uppercase tracking-wider border-l border-red-200 bg-red-50" colSpan={3}>{currentProvider || "Current"}</th>
                  <th className="px-3 py-2 text-center font-semibold text-[#3c68ff] uppercase tracking-wider border-l border-blue-200 bg-blue-50" colSpan={3}>Amplify HR</th>
                  <th className="px-3 py-2 text-center font-semibold text-emerald-700 uppercase tracking-wider border-l border-emerald-200 bg-emerald-50" colSpan={3}>Savings</th>
                </tr>
                <tr>
                  <th className="px-3 py-2 text-right font-medium text-red-500 border-l border-red-200 bg-red-50/60">
                    {getAdminColumnHeader(isAdp, adpUnbundled)}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-red-500 bg-red-50/60">SUTA</th>
                  <th className="px-3 py-2 text-right font-medium text-red-500 bg-red-50/60">WC</th>
                  <th className="px-3 py-2 text-right font-medium text-[#3c68ff]/70 border-l border-blue-200 bg-blue-50/60">Admin</th>
                  <th className="px-3 py-2 text-right font-medium text-[#3c68ff]/70 bg-blue-50/60">SUTA</th>
                  <th className="px-3 py-2 text-right font-medium text-[#3c68ff]/70 bg-blue-50/60">WC</th>
                  <th className="px-3 py-2 text-right font-medium text-emerald-600 border-l border-emerald-200 bg-emerald-50/60">
                    {getAdminColumnHeader(isAdp, adpUnbundled)}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-emerald-600 bg-emerald-50/60">SUTA</th>
                  <th className="px-3 py-2 text-right font-medium text-emerald-600 bg-emerald-50/60">WC</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item) => {
                  const c = calculate(item, sutaBases);
                  const p = proposedItems.find(pi => pi.id === item.id);
                  const pc = calculate(p, sutaBases);
                  const currentAdminPerItem = currentTotals.employees > 0
                    ? (currentAdminFee * item.employeeCount / currentTotals.employees) : 0;
                  const proposedAdminPerItem = proposedTotals.employees > 0
                    ? (proposedAdminFee * item.employeeCount / proposedTotals.employees) : 0;
                  const svcFeeRate = serviceFees[item.id]?.rate || 0;
                  const svcFeeAmt = (item.grossWages / 100) * svcFeeRate;
                  const currentAdminDisplay = isAdp && !adpUnbundled 
                    ? c.wcPremium + currentAdminPerItem 
                    : (isAdp && adpUnbundled ? svcFeeAmt : currentAdminPerItem);
                  const wcSave = c.wcPremium - pc.wcPremium;
                  const sutaSave = c.sutaTax - pc.sutaTax;
                  const adminSave = isAdp && !adpUnbundled
                    ? (c.wcPremium + currentAdminPerItem) - proposedAdminPerItem 
                    : (isAdp && adpUnbundled 
                      ? svcFeeAmt - proposedAdminPerItem  
                      : currentAdminPerItem - proposedAdminPerItem);
                  
                  return (
                    <tr key={item.id} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-medium">
                        <div className="flex items-center gap-1.5">
                          <span>{item.state}</span>
                          {isClientState(item.state) && (
                            <span className="text-[10px] font-bold text-amber-600 bg-amber-100 px-1 py-0.5 rounded" title="Client State: SUTA is a pass-through">C</span>
                          )}
                          {MONOPOLISTIC_STATES.includes(item.state) && (
                            <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-1 py-0.5 rounded" title="Monopolistic State">M</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-600">{item.wcCode}</td>
                      <td className="px-3 py-2 text-right font-mono">{item.employeeCount}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(item.grossWages)}</td>
                      {/* Current Costs */}
                      <td className="px-3 py-2 text-right font-mono border-l border-red-100 bg-red-50/30">{fmt(currentAdminDisplay)}</td>
                      <td className="px-3 py-2 text-right font-mono bg-red-50/30">{fmt(c.sutaTax)}</td>
                      <td className="px-3 py-2 text-right font-mono bg-red-50/30">{isAdp && !adpUnbundled ? "—" : fmt(c.wcPremium)}</td>
                      {/* Amplify Costs */}
                      <td className="px-3 py-2 text-right font-mono border-l border-blue-100 bg-blue-50/30">{fmt(proposedAdminPerItem)}</td>
                      <td className="px-3 py-2 text-right font-mono bg-blue-50/30">{fmt(pc.sutaTax)}</td>
                      <td className="px-3 py-2 text-right font-mono bg-blue-50/30">{fmt(pc.wcPremium)}</td>
                      {/* Savings */}
                      <td className={`px-3 py-2 text-right font-mono font-semibold border-l border-emerald-100 bg-emerald-50/30 ${getSavingsColor(adminSave)}`}>
                        {adminSave !== 0 ? fmt(adminSave) : "—"}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold bg-emerald-50/30 ${getSavingsColor(sutaSave)}`}>
                        {sutaSave !== 0 ? fmt(sutaSave) : "—"}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold bg-emerald-50/30 ${getSavingsColor(wcSave)}`}>
                        {isAdp && !adpUnbundled ? "—" : (wcSave !== 0 ? fmt(wcSave) : "—")}
                      </td>
                    </tr>
                  );
                })}
                {/* Totals Row */}
                <tr className="font-bold text-sm">
                  <td className="px-3 py-2.5 bg-slate-50" colSpan={2}>Totals</td>
                  <td className="px-3 py-2.5 text-right font-mono bg-slate-50">{currentTotals.employees}</td>
                  <td className="px-3 py-2.5 text-right font-mono bg-slate-50">{fmt(currentTotals.grossWages)}</td>
                  {/* Current Totals */}
                  <td className="px-3 py-2.5 text-right font-mono border-l border-red-100 bg-red-50/50">
                    {fmt(getCurrentAdminAmount(isAdp, adpUnbundled, currentTotals, currentAdminFee, totalServiceFees))}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono bg-red-50/50">{fmt(currentTotals.sutaTax)}</td>
                  <td className="px-3 py-2.5 text-right font-mono bg-red-50/50">{isAdp && !adpUnbundled ? "—" : fmt(currentTotals.wcPremium)}</td>
                  {/* Amplify Totals */}
                  <td className="px-3 py-2.5 text-right font-mono border-l border-blue-100 bg-blue-50/50">{fmt(proposedAdminFee)}</td>
                  <td className="px-3 py-2.5 text-right font-mono bg-blue-50/50">{fmt(proposedTotals.sutaTax)}</td>
                  <td className="px-3 py-2.5 text-right font-mono bg-blue-50/50">{fmt(proposedTotals.wcPremium)}</td>
                  {/* Savings Totals */}
                  {(() => {
                    const currentAdminTotal = getCurrentAdminAmount(isAdp, adpUnbundled, currentTotals, currentAdminFee, totalServiceFees);
                    const adminTotalSavings = currentAdminTotal - proposedAdminFee;
                    const sutaTotalSavings = currentTotals.sutaTax - proposedTotals.sutaTax;
                    const wcTotalSavings = currentTotals.wcPremium - proposedTotals.wcPremium;
                    return (
                      <>
                        <td className={`px-3 py-2.5 text-right font-mono border-l border-emerald-100 bg-emerald-50/50 ${getSavingsColor(adminTotalSavings)}`}>
                          {fmt(adminTotalSavings)}
                        </td>
                        <td className={`px-3 py-2.5 text-right font-mono bg-emerald-50/50 ${getSavingsColor(sutaTotalSavings)}`}>
                          {fmt(sutaTotalSavings)}
                        </td>
                        <td className={`px-3 py-2.5 text-right font-mono bg-emerald-50/50 ${getSavingsColor(wcTotalSavings)}`}>
                          {isAdp && !adpUnbundled ? "—" : fmt(wcTotalSavings)}
                        </td>
                      </>
                    );
                  })()}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footnotes */}
          {(isAdp || lineItems.some(i => isClientState(i.state)) || lineItems.some(i => MONOPOLISTIC_STATES.includes(i.state))) && (
            <div className="mt-4 p-4 bg-amber-50/50 border border-amber-200 rounded-lg text-xs text-amber-800 space-y-1">
              {isAdp && !adpUnbundled && (
                <p><strong>* ADP Service Fee:</strong> Includes bundled Workers' Comp and Admin Fee. Charges are calculated as a percent of gross wages.</p>
              )}
              {isAdp && adpUnbundled && (
                <p><strong>ADP TotalSource:</strong> Charges are calculated as a percent of gross wages.</p>
              )}
              {lineItems.some(i => isClientState(i.state)) && (
                <p><span className="inline-block text-[10px] font-bold text-amber-600 bg-amber-100 px-1 py-0.5 rounded mr-1">C</span><strong>= Client State:</strong> SUTA is a pass-through (client's own rate applies).</p>
              )}
              {lineItems.some(i => MONOPOLISTIC_STATES.includes(i.state)) && (
                <p><span className="inline-block text-[10px] font-bold text-purple-600 bg-purple-100 px-1 py-0.5 rounded mr-1">M</span><strong>= Monopolistic State:</strong> Workers' comp is state-managed (ND, OH, WA, WY).</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
